import React, {useCallback, useEffect} from 'react';
import {Form} from 'react-bootstrap';

import {useFormContext} from '../../utils/FormContext';
import {Hotkeys} from '../../utils/Keys';
import Col from '../Col';

import styles from './index.module.scss';

interface PasswordInputProps {
  name: string;
  value: string;
  label: string;
  sm?: number;
  md?: number;
  validate?: (value: string, label: string) => string | undefined;
  onChange: (value: string) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
  onBlur?: () => void;
}

export default function PasswordInput(props: PasswordInputProps) {
  const {name, value, label, sm, md, validate, onChange, onKeyPress, onEnter, onBlur} = props;
  const formState = useFormContext();

  const error = validate && validate(value, label);
  formState.setError(name, error);

  const handleKeyPress = onEnter
    ? (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === Hotkeys.ENTER) {
          if (formState.hasErrors()) {
            formState.setErrorVisible(name, error !== undefined);
            return;
          }
          onEnter();
        } else {
          onKeyPress && onKeyPress(e);
        }
      }
    : onKeyPress;

  const handleBlur = useCallback(() => {
    formState.setErrorVisible(name, error !== undefined);
    onBlur && onBlur();
  }, [formState, name, onBlur, error]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => formState.remove(name), [/*formState, */ name]);

  const shownError = formState.getShownError(name);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value);
  };

  const control = (
    <Form.Control
      placeholder={label}
      value={value}
      type="password"
      onChange={handleChange}
      className={styles.input}
      isInvalid={shownError !== undefined}
      onKeyPress={handleKeyPress}
      onBlur={handleBlur}
    />
  );

  return (
    <Col sm={sm} md={md} className={styles.inputGroup}>
      <Form.Group>
        {control}
        {shownError && <Form.Control.Feedback type="invalid">{shownError}</Form.Control.Feedback>}
      </Form.Group>
    </Col>
  );
}
